import React, { useState } from 'react';
import {
    Alert, Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { AppraisalService } from '../../services/appraisal.service';
import { useToast } from '../../context/ToastContext';
import { ICycleDetail, IReviewSummary } from '../../interfaces/appraisal.interface';
import { displayUsername } from '../../utils/userUtils';
import { isManagerDone } from './bands';
import { StatusChips } from './ReviewTable';
import { SingleUserPicker } from './UserPicker';
import AddParticipantsDialog from './AddParticipantsDialog';
import ConfirmDialog from './ConfirmDialog';

interface Props {
    institutionId: string;
    cycle: ICycleDetail;
    reviews: IReviewSummary[];
    onChanged: () => void;
}

/** Who is reviewed in the cycle and by whom. Editable until the cycle closes. */
const ParticipantsPanel: React.FC<Props> = ({ institutionId, cycle, reviews, onChanged }) => {
    const toast = useToast();
    const { t } = useTranslation(['appraisal', 'common']);
    const editable = cycle.status !== 'CLOSED';
    const [addOpen, setAddOpen] = useState(false);
    const [removing, setRemoving] = useState<IReviewSummary | null>(null);
    const [busy, setBusy] = useState(false);
    const missingReviewer = reviews.filter(r => !r.reviewer).length;

    const changeReviewer = async (review: IReviewSummary, reviewerUserId: string) => {
        try {
            await AppraisalService.setReviewer(institutionId, cycle.id, review.id, reviewerUserId);
            toast.showSuccess(t('participants.reviewerChanged'));
            onChanged();
        } catch (err) {
            toast.showApiError(err, t('participants.errors.reviewer'));
        }
    };

    const remove = async () => {
        if (!removing) return;
        setBusy(true);
        try {
            await AppraisalService.removeParticipant(institutionId, cycle.id, removing.id);
            toast.showSuccess(t('participants.removed'));
            setRemoving(null);
            onChanged();
        } catch (err) {
            toast.showApiError(err, t('participants.errors.remove'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography>{t('participants.count', { count: reviews.length })}</Typography>
                {editable && <Button variant="contained" onClick={() => setAddOpen(true)}>{t('participants.add')}</Button>}
            </Box>
            {editable && missingReviewer > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>{t('participants.missingReviewer', { count: missingReviewer })}</Alert>
            )}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('columns.employee')}</TableCell>
                            <TableCell>{t('columns.department')}</TableCell>
                            <TableCell>{t('columns.reviewer')}</TableCell>
                            <TableCell>{t('columns.status')}</TableCell>
                            {editable && <TableCell />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reviews.length === 0 ? (
                            <TableRow><TableCell colSpan={5}><Typography color="text.secondary">{t('participants.empty')}</Typography></TableCell></TableRow>
                        ) : reviews.map(review => {
                            const name = displayUsername(review.employee.name);
                            const canChangeReviewer = editable && !isManagerDone(review.status);
                            const canRemove = editable && review.status === 'PENDING';
                            return (
                                <TableRow key={review.id}>
                                    <TableCell>{name}</TableCell>
                                    <TableCell>{review.employee.departmentNames.join(', ') || '—'}</TableCell>
                                    <TableCell>
                                        {canChangeReviewer ? (
                                            <SingleUserPicker
                                                institutionId={institutionId}
                                                value={review.reviewer ? { id: review.reviewer.userId, username: review.reviewer.name } : null}
                                                onChange={u => changeReviewer(review, u.id)}
                                                excludeId={review.employee.userId}
                                                ariaLabel={t('participants.reviewerFor', { name })}
                                                placeholder={t('participants.noReviewer')}
                                            />
                                        ) : review.reviewer ? displayUsername(review.reviewer.name) : '—'}
                                    </TableCell>
                                    <TableCell><StatusChips review={review} /></TableCell>
                                    {editable && (
                                        <TableCell align="right">
                                            <Tooltip title={canRemove ? t('common:actions.remove') : t('participants.cannotRemove')}>
                                                <span>
                                                    <IconButton color="error" disabled={!canRemove} onClick={() => setRemoving(review)}
                                                        aria-label={t('participants.removeFor', { name })}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <AddParticipantsDialog
                open={addOpen}
                institutionId={institutionId}
                cycleId={cycle.id}
                onClose={() => setAddOpen(false)}
                onAdded={onChanged}
            />
            <ConfirmDialog
                open={!!removing}
                title={t('participants.confirmRemove.title')}
                body={t('participants.confirmRemove.body', { name: removing ? displayUsername(removing.employee.name) : '' })}
                confirmLabel={t('common:actions.remove')}
                danger
                busy={busy}
                onConfirm={remove}
                onClose={() => setRemoving(null)}
            />
        </Box>
    );
};

export default ParticipantsPanel;
